import React, { useEffect, useRef, useState, useMemo } from "react";
import * as d3 from "d3";
import { createEmotionalHeatmapData, getSongsInRadius } from '../dataAnalysis';
import { generateImage, generatePlaylistCover } from '../utils/sharingUtils';
import { getAudioFeatures as getAudioFeaturesFromApi } from '../services/emotifyService';
import { useAppContext } from '../context/AppContext';
import RecommendationsModal from './RecommendationsModal';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import InstagramIcon from '@mui/icons-material/Instagram';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import AlbumIcon from '@mui/icons-material/Album';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material';

// ============================================================================
// CONFIGURATION
// ============================================================================
const RANK_WEIGHTING_FACTOR = 0.25; // 0 = equal weights, 1 = balanced, >1 = more rank influence
const TRANSITION_DURATION = 1200;    // Transition duration in milliseconds (longer = smoother)
const SMOOTH_GRID_MULTIPLIER = 8;    // Multiplier for smooth rendering (higher = smoother but slower)
const TRANSITION_GRID_MULTIPLIER = 4; // Lower multiplier during transitions for better performance
const GAUSSIAN_BLUR_SIGMA = 3.5;

// Color palette for heatmap (defined once at module level)
const HEATMAP_COLORS = [
  [255, 255, 255], // Pure white
  [235, 235, 255], // Very light purple
  [210, 210, 255], // Light purple
  [170, 190, 255], // Bright blue
  [130, 210, 240], // Bright cyan
  [140, 240, 160], // Bright green
  [210, 250, 120], // Bright yellow-green
  [255, 240, 100], // Bright yellow
  [255, 200, 100], // Bright orange
  [255, 150, 110], // Brighter coral
  [255, 100, 100]  // Much brighter red
];

// Bilinear interpolation function (module-level for performance)
const bilinearInterpolate = (matrix: number[][], x: number, y: number, gridSize: number): number => {
  const x1 = Math.floor(x);
  const x2 = Math.min(x1 + 1, gridSize - 1);
  const y1 = Math.floor(y);
  const y2 = Math.min(y1 + 1, gridSize - 1);
  
  if (x1 < 0 || y1 < 0 || x1 >= gridSize || y1 >= gridSize) return 0;
  
  const dx = x - x1;
  const dy = y - y1;
  
  const v11 = matrix[y1]?.[x1] || 0;
  const v12 = matrix[y2]?.[x1] || 0;
  const v21 = matrix[y1]?.[x2] || 0;
  const v22 = matrix[y2]?.[x2] || 0;
  
  return Math.max(0, v11 * (1 - dx) * (1 - dy) + v21 * dx * (1 - dy) + v12 * (1 - dx) * dy + v22 * dx * dy);
};

// Dark mode color palette — low values fade to card background (#1B1B30)
const HEATMAP_COLORS_DARK = [
  [27, 27, 48],     // #1B1B30 — matches card background exactly
  [30, 28, 65],     // Very dark indigo tint
  [45, 38, 90],     // Dark indigo
  [55, 50, 130],    // Deep indigo-blue
  [40, 90, 140],    // Dark cyan
  [35, 140, 80],    // Dark green
  [110, 160, 50],   // Dark yellow-green
  [180, 170, 40],   // Dark yellow
  [200, 140, 50],   // Dark orange
  [220, 100, 70],   // Coral
  [240, 70, 70]     // Bright red
];

// Color interpolation function (module-level for performance)
const getHeatmapColor = (t: number, dark: boolean = false): string => {
  if (t <= 0.001) return dark ? "rgba(27, 27, 48, 0)" : "rgba(255, 255, 255, 0)";
  
  const colors = dark ? HEATMAP_COLORS_DARK : HEATMAP_COLORS;
  const scaledT = Math.min(1, Math.max(0, t));
  const index = scaledT * (colors.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const fraction = index - lower;
  
  if (lower === upper) {
    const [r, g, b] = colors[lower];
    const opacity = dark ? Math.pow(scaledT, 0.3) * 0.95 : Math.pow(scaledT, 0.5) * 0.95;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  
  const [r1, g1, b1] = colors[lower];
  const [r2, g2, b2] = colors[upper];
  const r = Math.round(r1 + (r2 - r1) * fraction);
  const g = Math.round(g1 + (g2 - g1) * fraction);
  const b = Math.round(b1 + (b2 - b1) * fraction);
  
  const opacity = dark ? Math.pow(scaledT, 0.3) * 0.95 : Math.pow(scaledT, 0.5) * 0.95;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};
// ============================================================================

interface HeatmapProps {
  data: { x: number; y: number; value: number; songs?: Array<{ name: string; artist: string; rank: number; valence: number; energy: number }> }[];
  audioFeatures?: any[];
  triggerShare?: boolean;
}

const Heatmap: React.FC<HeatmapProps> = ({ data, audioFeatures, triggerShare }) => {
  const { playlistInfo, platform: musicPlatform } = useAppContext();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const isInitialRenderRef = useRef(true);
  const [heatmapVisible, setHeatmapVisible] = useState(false);
  const [precomputedData, setPrecomputedData] = useState<{ [key: string]: any }>({});
  const [currentTimeRange, setCurrentTimeRange] = useState<string>('long_term');
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const animationFrameRef = useRef<number | null>(null);
  const previousMatrixRef = useRef<number[][] | null>(null);
  const previousTimeRangeRef = useRef<string>('long_term');
  const [tooltipPinned, setTooltipPinned] = useState<boolean>(false);
  // Mirror of tooltipPinned for the canvas event handlers to read. Keeping the
  // handlers off the state value lets us drop tooltipPinned from the main render
  // effect's deps — otherwise every pin/unpin re-runs the effect and clears the
  // canvas (flicker). See the render effect's dependency array below.
  const tooltipPinnedRef = useRef<boolean>(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false);
  const lastTooltipUpdateRef = useRef<number>(0);
  const tooltipHideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 760, height: 760 });
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  
  // Subheading animation state - simple current/next model
  const [currentSubheading, setCurrentSubheading] = useState<string>('');
  const [nextSubheading, setNextSubheading] = useState<string | null>(null);
  const [isSubheadingAnimating, setIsSubheadingAnimating] = useState<boolean>(false);
  
  // Recommendations modal state
  const [showRecommendationsModal, setShowRecommendationsModal] = useState<boolean>(false);
  const [modalContext, setModalContext] = useState<{
    valence: number;
    energy: number;
    nearbySongs: Array<{
      id: string;
      name: string;
      artist: string;
      spotifyId: string;
    }>;
  } | null>(null);
  
  // Mode selection state - simplified for public playlist mode
  const [, setShowPlaylistDropdown] = useState<boolean>(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState<any | null>(null);
  const [, setIsLoadingPlaylistData] = useState<boolean>(false);
  const [playlistHeatmapData, setPlaylistHeatmapData] = useState<any>(null);
  const playlistDataCacheRef = useRef<Map<string, any>>(new Map());

  const timeRangeOptions = useMemo(() => [
    { key: 'long_term', label: 'Past Year', description: 'Your consistent ones' },
    { key: 'medium_term', label: 'Past 6 Months', description: 'Your current taste' },
    { key: 'short_term', label: 'Past Month', description: 'Your recent favorites' }
  ], []);

  // Helper to trigger subheading transition
  const animateSubheading = (newText: string) => {
    if (newText === currentSubheading) return; // No change needed
    
    setNextSubheading(newText);
    setIsSubheadingAnimating(true);
    
    // After animation completes, swap current with next
    setTimeout(() => {
      setCurrentSubheading(newText);
      setNextSubheading(null);
      setIsSubheadingAnimating(false);
    }, TRANSITION_DURATION);
  };

  // Helper to get subheading text for a given state
  const getSubheadingText = (playlistName?: string) => {
    if (playlistName) {
      return playlistName;
    }
    return '';
  };

  // Memoized music distribution analysis - only recalculates when data changes
  const analysis = useMemo(() => {
    const currentData = playlistHeatmapData || precomputedData[currentTimeRange];
    if (!currentData || !currentData.features || !currentData.heatmapData) return null;

    const features = currentData.features;
    const heatmapData = currentData.heatmapData;
    
    // Calculate average valence and energy (normalized to -1 to 1 range)
    const avgValence = (features.reduce((sum: number, f: any) => sum + f.valence, 0) / features.length) * 2 - 1;
    const avgEnergy = (features.reduce((sum: number, f: any) => sum + f.energy, 0) / features.length) * 2 - 1;
    
    // Calculate standard deviation to measure spread
    const normalizedValences = features.map((f: any) => f.valence * 2 - 1);
    const normalizedEnergies = features.map((f: any) => f.energy * 2 - 1);
    
    const valenceVariance = normalizedValences.reduce((sum: number, v: number) => sum + Math.pow(v - avgValence, 2), 0) / features.length;
    const energyVariance = normalizedEnergies.reduce((sum: number, e: number) => sum + Math.pow(e - avgEnergy, 2), 0) / features.length;
    const valenceStd = Math.sqrt(valenceVariance);
    const energyStd = Math.sqrt(energyVariance);
    const avgStd = (valenceStd + energyStd) / 2;
    
    // Calculate density in each quadrant from heatmap matrix
    const matrix = heatmapData.matrix;
    const gridSize = matrix.length;
    const quadrantDensity = {
      turbulent: 0,    // low valence, high energy (top-left)
      energetic: 0,    // high valence, high energy (top-right)
      melancholic: 0,  // low valence, low energy (bottom-left)
      peaceful: 0      // high valence, low energy (bottom-right)
    };
    
    // Sum up density values in each quadrant
    // IMPORTANT: In the matrix storage, y=0 is LOW energy (bottom), y=gridSize-1 is HIGH energy (top)
    // This is because the rendering flips it: flippedOrigI = gridSizeMinusOne - origI
    // x-axis: left (low valence) to right (high valence) - NOT flipped
    // y-axis: matrix[0] = low energy (bottom), matrix[gridSize-1] = high energy (top)
    const halfGrid = Math.floor(gridSize / 2);
    for (let y = 0; y < gridSize; y++) {
      for (let x = 0; x < gridSize; x++) {
        const value = matrix[y][x] || 0;
        const isBottomHalf = y < halfGrid;   // Low energy (matrix[0] to matrix[halfGrid])
        const isRightHalf = x >= halfGrid;   // High valence (right of visual)
        
        if (!isBottomHalf && !isRightHalf) {
          quadrantDensity.turbulent += value;      // Top-left: low valence, high energy
        } else if (!isBottomHalf && isRightHalf) {
          quadrantDensity.energetic += value;      // Top-right: high valence, high energy
        } else if (isBottomHalf && !isRightHalf) {
          quadrantDensity.melancholic += value;    // Bottom-left: low valence, low energy
        } else {
          quadrantDensity.peaceful += value;       // Bottom-right: high valence, low energy
        }
      }
    }
    
    // Find dominant quadrant based on density
    const dominantQuadrant = Object.entries(quadrantDensity).reduce((a, b) => 
      quadrantDensity[a[0] as keyof typeof quadrantDensity] > quadrantDensity[b[0] as keyof typeof quadrantDensity] ? a : b
    )[0];
    
    const quadrantNames = {
      turbulent: "Turbulent",
      energetic: "Energetic",
      melancholic: "Melancholic",
      peaceful: "Peaceful"
    };
    
    const quadrant = quadrantNames[dominantQuadrant as keyof typeof quadrantNames];
    
    // Calculate percentage based on density
    const totalDensity = Object.values(quadrantDensity).reduce((sum, val) => sum + val, 0);
    const quadrantPercentage = totalDensity > 0 
      ? Math.round((quadrantDensity[dominantQuadrant as keyof typeof quadrantDensity] / totalDensity) * 100)
      : 0;
    
    // Calculate how balanced the distribution is
    const sortedDensities = Object.values(quadrantDensity).sort((a, b) => b - a);
    const topQuadrant = sortedDensities[0];
    const secondQuadrant = sortedDensities[1];
    const thirdQuadrant = sortedDensities[2];
    
    // Determine diversity based on spread AND distribution
    let diversity = "";
    let diversityDescription = "";
    
    // Check if music is actually distributed across quadrants (stricter thresholds)
    const hasGoodDistribution = secondQuadrant > totalDensity * 0.30 && thirdQuadrant > totalDensity * 0.20;
    const hasModerateDistribution = secondQuadrant > totalDensity * 0.25 && thirdQuadrant > totalDensity * 0.15;
    
    if (avgStd > 0.5 && hasGoodDistribution) {
      diversity = "Eclectic";
      diversityDescription = "Your music spans across many different moods and emotional landscapes";
    } else if (avgStd > 0.4 && hasGoodDistribution) {
      diversity = "Varied";
      diversityDescription = "You enjoy a mix of different emotional tones in your music";
    } else if (avgStd > 0.35 && hasModerateDistribution) {
      diversity = "Varied";
      diversityDescription = "Your music explores different emotional spaces within this mood";
    } else {
      diversity = "Focused";
      diversityDescription = "Your music taste is consistent and gravitates toward a specific emotional space";
    }
    
    // Get context label
    const contextLabel = selectedPlaylist 
      ? `playlist "${selectedPlaylist.name}"`
      : 'this playlist';
    
    // Create description based on dominant quadrant and distribution
    let description = "";
    
    // Determine balance level
    const isVeryDominated = topQuadrant > totalDensity * 0.6;
    const isDominated = topQuadrant > totalDensity * 0.45;
    const isBalanced = topQuadrant < totalDensity * 0.35 && secondQuadrant > totalDensity * 0.25;
    const isWellBalanced = topQuadrant < totalDensity * 0.30 && thirdQuadrant > totalDensity * 0.20;
    
    if (isVeryDominated) {
      description = `In ${contextLabel}, the music is strongly ${quadrant.toLowerCase()}, with ${quadrantPercentage}% of the listening intensity in this emotional space`;
    } else if (isDominated) {
      description = `The music in ${contextLabel} leans ${quadrant.toLowerCase()}, with ${quadrantPercentage}% of the listening intensity carrying this mood`;
    } else if (isWellBalanced) {
      description = `${contextLabel} is well-balanced across moods, with ${quadrant.toLowerCase()} being slightly more prominent at ${quadrantPercentage}%`;
    } else if (isBalanced) {
      description = `${contextLabel} spans multiple moods, with ${quadrant.toLowerCase()} leading at ${quadrantPercentage}%`;
    } else {
      description = `The music in ${contextLabel} gravitates toward ${quadrant.toLowerCase()}, representing ${quadrantPercentage}% of the listening intensity`;
    }
    
    // Get sample songs from top tracks
    const sampleSongs = features.slice(0, 3).map((f: any) => 
      f.name || f.track_name || 'Unknown'
    );
    
    return {
      quadrant,
      diversity,
      diversityDescription,
      description,
      avgValence,
      avgEnergy,
      spread: avgStd,
      quadrantPercentage,
      sampleSongs,
      timeRangeLabel: 'Playlist'
    };
  }, [playlistHeatmapData, precomputedData, currentTimeRange, selectedPlaylist]);

  // Responsive canvas sizing
  useEffect(() => {
    const updateCanvasSize = () => {
      const isMobile = window.innerWidth < 768;
      // Account for container padding (20px on each side = 40px total)
      const maxSize = isMobile ? Math.min(window.innerWidth - 80, 600) : 760;
      setCanvasSize({ width: maxSize, height: maxSize });
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Keep the ref in sync so canvas handlers read the latest pinned state
  // without the render effect depending on tooltipPinned.
  useEffect(() => {
    tooltipPinnedRef.current = tooltipPinned;
  }, [tooltipPinned]);

  // Open share modal when triggerShare prop changes to true
  useEffect(() => {
    if (triggerShare) {
      setShowShareModal(true);
    }
  }, [triggerShare]);

  const handleDownloadImage = async () => {
    const currentData = playlistHeatmapData || precomputedData[currentTimeRange];
    if (!currentData || !canvasRef.current) return;
    
    setIsGeneratingImage(true);
    setShowShareModal(false);
    
    try {
      await generateImage(
        currentTimeRange, 
        timeRangeOptions, 
        canvasRef.current, 
        null, 
        analysis, 
        currentData.features, 
        'download',
        undefined,
        'playlist',
        playlistInfo?.name,
        playlistInfo?.image,
        playlistInfo?.owner,
        musicPlatform || undefined,
        isDark
      );
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleSaveCover = async () => {
    if (!canvasRef.current) return;

    setIsGeneratingImage(true);
    setShowShareModal(false);

    try {
      await generatePlaylistCover(canvasRef.current, isDark, musicPlatform || undefined);
    } catch (error) {
      console.error('Error generating playlist cover:', error);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleShareImage = async (platform?: 'instagram') => {
    const currentData = playlistHeatmapData || precomputedData[currentTimeRange];
    if (!currentData || !canvasRef.current) return;
    
    setIsGeneratingImage(true);
    setShowShareModal(false);
    
    try {
      await generateImage(
        currentTimeRange, 
        timeRangeOptions, 
        canvasRef.current, 
        null, 
        analysis, 
        currentData.features, 
        'share', 
        platform,
        'playlist',
        playlistInfo?.name,
        playlistInfo?.image,
        playlistInfo?.owner,
        musicPlatform || undefined,
        isDark
      );
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Precompute heatmap data from audioFeatures (provided by context)
  useEffect(() => {
    if (!audioFeatures || audioFeatures.length === 0) return;
    if (Object.keys(precomputedData).length > 0) return; // Already computed
    
    const heatmapData = createEmotionalHeatmapData(audioFeatures, 80, GAUSSIAN_BLUR_SIGMA, RANK_WEIGHTING_FACTOR);

    setPrecomputedData({
      current: { heatmapData, features: audioFeatures }
    });
    setCurrentTimeRange('current');
    
    // Trigger fade-in on initial mount
    if (isInitialRenderRef.current) {
      setTimeout(() => {
        setHeatmapVisible(true);
        isInitialRenderRef.current = false;
      }, 100);
    }
  }, [audioFeatures, precomputedData]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setShowPlaylistDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Function to fetch and process playlist data
  const fetchPlaylistData = async (playlistId: string, playlistName: string) => {
    // Check if we already have this playlist data cached
    if (playlistDataCacheRef.current.has(playlistId)) {
      const cachedData = playlistDataCacheRef.current.get(playlistId);
      
      // Store current matrix as previous for transition
      if (precomputedData[currentTimeRange]?.heatmapData?.matrix) {
        previousMatrixRef.current = precomputedData[currentTimeRange].heatmapData.matrix.map((row: number[]) => [...row]);
      } else if (playlistHeatmapData?.heatmapData?.matrix) {
        previousMatrixRef.current = playlistHeatmapData.heatmapData.matrix.map((row: number[]) => [...row]);
      }
      
      // Trigger subheading animation
      animateSubheading(getSubheadingText(playlistName));
      
      // Start transition
      setIsTransitioning(true);
      setPlaylistHeatmapData(cachedData);
      
      // End transition after animation completes
      setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
      
      return;
    }
    
    setIsLoadingPlaylistData(true);
    
    try {
      const { describePlaylist } = await import('../services/emotifyService');
      const result = await describePlaylist(playlistId);
      if (result.error || !result.data) {
        console.error('Failed to load playlist:', result.error);
        setIsLoadingPlaylistData(false);
        return;
      }

      const trackIds = result.data.tracks.map(t => t.id);
      const trackMap = new Map(result.data.tracks.map(t => [t.id, t]));

      // Build a map of existing audio features from current data
      const existingFeaturesMap = new Map();
      if (audioFeatures) {
        audioFeatures.forEach((feature: any) => {
          const spotifyId = feature.spotify_id || feature.id;
          if (spotifyId && !existingFeaturesMap.has(spotifyId)) {
            existingFeaturesMap.set(spotifyId, feature);
          }
        });
      }

      // Match playlist tracks with existing features
      const enrichedFeatures: any[] = [];
      const missingTrackIds: string[] = [];

      trackIds.forEach((trackId: string, index: number) => {
        const existingFeature = existingFeaturesMap.get(trackId);
        if (existingFeature) {
          enrichedFeatures.push({
            ...existingFeature,
            rank: index + 1,
            spotify_id: trackId
          });
        } else {
          missingTrackIds.push(trackId);
        }
      });

      // Fetch missing audio features from ReccoBeats if needed (batched by 40)
      if (missingTrackIds.length > 0) {
        const BATCH_SIZE = 40;
        const allFetchedFeatures: any[] = [];
        
        // Process in batches of 40
        for (let i = 0; i < missingTrackIds.length; i += BATCH_SIZE) {
          const batchIds = missingTrackIds.slice(i, i + BATCH_SIZE);
          const audioFeaturesResult = await getAudioFeaturesFromApi(batchIds);
          if (audioFeaturesResult.data) {
            allFetchedFeatures.push(...audioFeaturesResult.data.audioFeatures);
          }
        }
        
        // Process all fetched features
        allFetchedFeatures.forEach((feature: any) => {
          const trackIndex = trackIds.indexOf(feature.spotify_id);
          if (trackIndex !== -1) {
            enrichedFeatures.push({
              ...feature,
              rank: trackIndex + 1
            });
          }
        });
      }

      // Sort by original playlist order
      enrichedFeatures.sort((a, b) => a.rank - b.rank);

      // Create heatmap data
      const heatmapData = createEmotionalHeatmapData(enrichedFeatures, 80, GAUSSIAN_BLUR_SIGMA, RANK_WEIGHTING_FACTOR);

      const newPlaylistData = {
        heatmapData,
        features: enrichedFeatures,
        playlistName
      };

      // Store current state as previous before changing (for transition)
      if (precomputedData[currentTimeRange]?.heatmapData?.matrix) {
        previousMatrixRef.current = precomputedData[currentTimeRange].heatmapData.matrix.map((row: number[]) => [...row]);
      } else if (playlistHeatmapData?.heatmapData?.matrix) {
        previousMatrixRef.current = playlistHeatmapData.heatmapData.matrix.map((row: number[]) => [...row]);
      }
      
      // Trigger subheading animation
      animateSubheading(getSubheadingText(playlistName));

      // Start transition
      setIsTransitioning(true);

      // Cache the playlist data
      playlistDataCacheRef.current.set(playlistId, newPlaylistData);
      setPlaylistHeatmapData(newPlaylistData);

      // End transition after animation completes
      setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_DURATION);

    } catch (error) {
      console.error('Error fetching playlist data:', error);
    } finally {
      setIsLoadingPlaylistData(false);
    }
  };

  // Handle playlist selection
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePlaylistSelect = async (playlist: any) => {
    setSelectedPlaylist(playlist);
    setShowPlaylistDropdown(false);
    await fetchPlaylistData(playlist.id, playlist.name);
  };

  // Main rendering effect with Canvas + SVG hybrid
  useEffect(() => {
    if (!canvasRef.current || !svgRef.current) return;
    
    const currentData = playlistHeatmapData || precomputedData[currentTimeRange];
    if (!currentData) return;
    
    const { heatmapData } = currentData;
    if (!heatmapData.matrix || heatmapData.matrix.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const svg = d3.select(svgRef.current);
    
    // Dimensions - centered with equal margins (responsive)
    const isMobile = window.innerWidth < 768;
    const margin = { top: isMobile ? 30 : 50, right: isMobile ? 30 : 50, bottom: isMobile ? 30 : 50, left: isMobile ? 30 : 50 };
    const width = canvasSize.width - margin.left - margin.right;
    const height = canvasSize.height - margin.top - margin.bottom;

    // Set canvas size (high DPI support). Only reassign the backing-store size
    // when it actually changes — writing canvas.width/height clears the canvas,
    // so an unconditional reset here flashes on every effect re-run (e.g. theme
    // recolor). renderHeatmap repaints the full frame regardless.
    const dpr = window.devicePixelRatio || 1;
    const targetW = width * dpr;
    const targetH = height * dpr;
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }

    // Reset transform before scaling to prevent accumulation on resize
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    // Set SVG dimensions
    svg.attr("width", canvasSize.width)
       .attr("height", canvasSize.height);

    // Create scales
    const gridSize = heatmapData.matrix.length;

    // Calculate max value for color scaling
    const flatMatrix = heatmapData.matrix.flat().filter((val: any) => typeof val === 'number') as number[];
    const maxValue = flatMatrix.length > 0 ? (d3.max(flatMatrix) || 1) : 1;
    
    // Create color scale using module-level color function
    const colorScale = (value: number) => getHeatmapColor(value / maxValue, isDark);

    // Render heatmap to canvas with smooth data interpolation
    // Use lower resolution during transitions for better performance
    const currentMultiplier = isTransitioning ? TRANSITION_GRID_MULTIPLIER : SMOOTH_GRID_MULTIPLIER;
    const smoothGridSize = gridSize * currentMultiplier;
    const cellWidth = width / smoothGridSize;
    const cellHeight = height / smoothGridSize;

    // Pre-calculate cell dimensions to avoid repeated calculations
    const cellWidthCeil = Math.ceil(cellWidth) + 1;
    const cellHeightCeil = Math.ceil(cellHeight) + 1;
    const gridSizeMinusOne = gridSize - 1;
    const smoothGridSizeMinusOne = smoothGridSize - 1;

    // Function to render heatmap with interpolated matrix values
    const renderHeatmap = (matrix: number[][], progress: number = 1) => {
      // Clear canvas with theme-aware background
      ctx.fillStyle = isDark ? '#1B1B30' : '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Use typed array for better performance during interpolation
      const hasTransition = isTransitioning && previousMatrixRef.current && progress < 1;
      
      for (let i = 0; i < smoothGridSize; i++) {
        const origI = (i / smoothGridSizeMinusOne) * gridSizeMinusOne;
        const flippedOrigI = gridSizeMinusOne - origI;
        const y = (i / smoothGridSize) * height;
        
        for (let j = 0; j < smoothGridSize; j++) {
          const origJ = (j / smoothGridSizeMinusOne) * gridSizeMinusOne;
          
          let value: number;
          
          // Interpolate between previous and current matrix if transitioning
          if (hasTransition) {
            const prevValue = bilinearInterpolate(previousMatrixRef.current!, origJ, flippedOrigI, gridSize);
            const currValue = bilinearInterpolate(matrix, origJ, flippedOrigI, gridSize);
            value = prevValue * (1 - progress) + currValue * progress;
          } else {
            value = bilinearInterpolate(matrix, origJ, flippedOrigI, gridSize);
          }
          
          if (value <= 0.0001) continue;
          
          const x = (j / smoothGridSize) * width;
          
          ctx.fillStyle = colorScale(value);
          ctx.fillRect(x, y, cellWidthCeil, cellHeightCeil);
        }
      }
    };

    // Animate transition if transitioning
    if (isTransitioning) {
      if (previousMatrixRef.current) {
        const startTime = performance.now();
        let lastFrameTime = startTime;
        
        const animate = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / TRANSITION_DURATION, 1);
          
          // Use a smoother easing function for more organic feel
          const eased = d3.easeCubicInOut(progress);
          
          // Only render if enough time has passed (throttle to ~60fps)
          const timeSinceLastFrame = currentTime - lastFrameTime;
          if (timeSinceLastFrame >= 16 || progress >= 1) {
            renderHeatmap(heatmapData.matrix, eased);
            lastFrameTime = currentTime;
          }
          
          if (progress < 1) {
            // Continue animation
            animationFrameRef.current = requestAnimationFrame(animate);
          }
        };
        
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // No previous matrix, just render current
        renderHeatmap(heatmapData.matrix, 1);
      }
      // Don't render in else branch during transition - animation handles it
    } else {
      // Not transitioning - render current state and update refs
      renderHeatmap(heatmapData.matrix, 1);
      previousMatrixRef.current = heatmapData.matrix.map((row: number[]) => [...row]);
      previousTimeRangeRef.current = currentTimeRange;
    }

    // Setup SVG overlay for box, crosshairs, and labels
    const isFirstRender = svg.selectAll("g.main-group").empty();
    if (isFirstRender) {
      svg.selectAll("*").remove();
    }

    let g = svg.select<SVGGElement>("g.main-group");
    if (g.empty()) {
      g = svg.append("g")
        .attr("class", "main-group");
    }
    // Always update transform in case margins changed
    g.attr("transform", `translate(${margin.left},${margin.top})`);

    // Add border box with rounded corners (update on resize)
    let borderBox = g.select<SVGRectElement>("rect.border-box");
    if (borderBox.empty()) {
      borderBox = g.append("rect")
        .attr("class", "border-box")
        .attr("fill", "none")
        .attr("stroke", isDark ? "#444" : "#cccccc")
        .attr("stroke-width", 2)
        .attr("rx", 16)
        .attr("ry", 16);
    } else {
      borderBox.attr("stroke", isDark ? "#444" : "#cccccc");
    }
    // Always update dimensions
    borderBox
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width)
      .attr("height", height);

    // Add crosshairs at (0, 0) - center of the chart
    const centerX = width / 2;
    const centerY = height / 2;

    const crosshairColor = isDark ? '#666' : '#999999';

    let verticalLine = g.select<SVGLineElement>("line.vertical-crosshair");
    if (verticalLine.empty()) {
      verticalLine = g.append("line")
        .attr("class", "vertical-crosshair")
        .attr("stroke", crosshairColor)
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "5,5");
    } else {
      verticalLine.attr("stroke", crosshairColor);
    }
    verticalLine
      .attr("x1", centerX)
      .attr("x2", centerX)
      .attr("y1", 0)
      .attr("y2", height);

    let horizontalLine = g.select<SVGLineElement>("line.horizontal-crosshair");
    if (horizontalLine.empty()) {
      horizontalLine = g.append("line")
        .attr("class", "horizontal-crosshair")
        .attr("stroke", crosshairColor)
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "5,5");
    } else {
      horizontalLine.attr("stroke", crosshairColor);
    }
    horizontalLine
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", centerY)
      .attr("y2", centerY);

    // Title removed - user requested to remove the heading above heatmap

    // Add quadrant labels - pill edges positioned at equal distance from heatmap edges
    const distance = isMobile ? 16 : 20; // Distance from heatmap edge to pill edge
    const labelFontSize = isMobile ? 10 : 12;
    const pillRadius = 8;
    const bgPadding = isMobile ? 10 : 12;
    const bgVerticalPadding = isMobile ? 5 : 6;
    ctx.font = `600 ${labelFontSize}px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    
    // We need to position text, then adjust based on pill background size
    // For now, create temporary positions to measure
    const labels = [
      { label: "Turbulent", corner: 'top-left' as const },
      { label: "Energetic", corner: 'top-right' as const },
      { label: "Melancholic", corner: 'bottom-left' as const },
      { label: "Peaceful", corner: 'bottom-right' as const }
    ];

    labels.forEach((item, idx) => {
      let labelGroup = g.select<SVGGElement>(`g.quadrant-${idx}`);
      if (labelGroup.empty()) {
        labelGroup = g.append("g").attr("class", `quadrant-${idx}`);

        labelGroup.append("rect").attr("class", "quadrant-bg");
        labelGroup.append("text")
          .attr("class", "quadrant-label")
          .style("fill", isDark ? "#bbb" : "#555")
          .style("letter-spacing", "0.5px");
      } else {
        labelGroup.select("text.quadrant-label")
          .style("fill", isDark ? "#bbb" : "#555");
      }

      // Set text properties first
      const textElement = labelGroup.select<SVGTextElement>("text.quadrant-label")
        .style("font-size", `${labelFontSize}px`)
        .style("font-weight", "600")
        .text(item.label);

      // Measure text to calculate pill size
      const textMetrics = ctx.measureText(item.label);
      const pillWidth = textMetrics.width + bgPadding * 2;
      const pillHeight = labelFontSize + bgVerticalPadding * 2;
      
      // Calculate pill background position based on corner
      let pillX: number, pillY: number;
      let textX: number, textY: number;
      
      switch (item.corner) {
        case 'top-left':
          // Pill top-left at (distance, distance)
          pillX = distance;
          pillY = distance;
          textX = pillX + pillWidth / 2;
          textY = pillY + pillHeight / 2 + labelFontSize * 0.35; // Center vertically
          break;
        case 'top-right':
          // Pill top-right at (width - distance, distance)
          pillX = width - distance - pillWidth;
          pillY = distance;
          textX = pillX + pillWidth / 2;
          textY = pillY + pillHeight / 2 + labelFontSize * 0.35;
          break;
        case 'bottom-left':
          // Pill bottom-left at (distance, height - distance)
          pillX = distance;
          pillY = height - distance - pillHeight;
          textX = pillX + pillWidth / 2;
          textY = pillY + pillHeight / 2 + labelFontSize * 0.35;
          break;
        case 'bottom-right':
          // Pill bottom-right at (width - distance, height - distance)
          pillX = width - distance - pillWidth;
          pillY = height - distance - pillHeight;
          textX = pillX + pillWidth / 2;
          textY = pillY + pillHeight / 2 + labelFontSize * 0.35;
          break;
      }
      
      // Position text - centered in pill
      textElement
        .attr("x", textX)
        .attr("y", textY)
        .attr("text-anchor", "middle");
      
      // Position pill background
      labelGroup.select("rect.quadrant-bg")
        .attr("x", pillX)
        .attr("y", pillY)
        .attr("width", pillWidth)
        .attr("height", pillHeight)
        .attr("fill", isDark ? "rgba(40, 40, 40, 0.92)" : "rgba(255, 255, 255, 0.95)")
        .attr("stroke", isDark ? "rgba(255,255,255,0.15)" : "#d0d0d0")
        .attr("stroke-width", 1)
        .attr("rx", pillRadius)
        .attr("filter", isDark ? "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" : "drop-shadow(0 2px 4px rgba(0,0,0,0.08))");
    });

    // Tooltip setup
    const tooltipBg = isDark ? 'rgba(30, 30, 30, 0.98)' : 'rgba(255, 255, 255, 0.98)';
    const tooltipColor = isDark ? '#E0E0E0' : '#000';
    const tooltipBorder = isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)';
    const tooltipShadow = isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.15)';

    let tooltip = d3.select("body").select<HTMLDivElement>("div.heatmap-tooltip");
    if (tooltip.empty()) {
      tooltip = d3.select("body").append("div")
        .attr("class", "heatmap-tooltip")
        .style("position", "absolute")
        .style("visibility", "hidden")
        .style("background", tooltipBg)
        .style("color", tooltipColor)
        .style("padding", isMobile ? "10px" : "12px")
        .style("border-radius", "24px")
        .style("font-size", isMobile ? "11px" : "12px")
        .style("font-family", 'DM Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif')
        .style("line-height", "1.4")
        .style("pointer-events", "auto")
        .style("z-index", "1000")
        .style("max-width", isMobile ? "280px" : "320px")
        .style("box-shadow", tooltipShadow)
        .style("border", tooltipBorder);
    } else {
      tooltip
        .style("background", tooltipBg)
        .style("color", tooltipColor)
        .style("padding", isMobile ? "10px" : "12px")
        .style("font-size", isMobile ? "11px" : "12px")
        .style("max-width", isMobile ? "280px" : "320px")
        .style("box-shadow", tooltipShadow)
        .style("border", tooltipBorder);
    }

    // Throttled tooltip update function
    const updateTooltip = (clientX: number, clientY: number, pageX: number, pageY: number) => {
      const now = performance.now();
      const timeSinceLastUpdate = now - lastTooltipUpdateRef.current;
      
      // Always update position, throttle content updates
      const shouldUpdateContent = timeSinceLastUpdate >= 33;
      
      if (shouldUpdateContent) {
        lastTooltipUpdateRef.current = now;
      }
      
      // Calculate position relative to cursor
      const rect = canvas.getBoundingClientRect();
      const mouseX = clientX - rect.left;
      const mouseY = clientY - rect.top;
      
      // Check if mouse is within canvas bounds
      if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) {
        // Mouse is outside canvas - hide tooltip
        tooltip.style("visibility", "hidden");
        return;
      }
      
      // Clamp valence and energy to valid range [-1, 1]
      const calculatedValence = Math.max(-1, Math.min(1, (mouseX / width) * 2 - 1));
      const calculatedEnergy = Math.max(-1, Math.min(1, ((height - mouseY) / height) * 2 - 1));
      
      // Get actual tooltip dimensions
      const tooltipNode = tooltip.node();
      const tooltipWidth = tooltipNode ? tooltipNode.offsetWidth : (isMobile ? 280 : 320);
      const tooltipHeight = tooltipNode ? tooltipNode.offsetHeight : 300;
      const offset = isMobile ? 60 : 10; // Larger offset on mobile to avoid finger
      const padding = 10; // Padding from edges
      
      // Get heatmap boundaries in page coordinates
      const heatmapLeft = rect.left;
      const heatmapRight = rect.right;
      const heatmapTop = rect.top;
      const heatmapBottom = rect.bottom;
      
      // On mobile, use screen bounds; on desktop, use heatmap bounds
      const boundsLeft = isMobile ? padding : heatmapLeft + padding;
      const boundsRight = isMobile ? window.innerWidth - padding : heatmapRight - padding;
      const boundsTop = isMobile ? padding : heatmapTop + padding;
      const boundsBottom = isMobile ? window.innerHeight - padding : heatmapBottom - padding;
      
      let tooltipX: number;
      let tooltipY: number;
      
      // Horizontal positioning
      if (pageX + offset + tooltipWidth <= boundsRight) {
        // Fits to the right
        tooltipX = pageX + offset;
      } else if (pageX - offset - tooltipWidth >= boundsLeft) {
        // Fits to the left
        tooltipX = pageX - tooltipWidth - offset;
      } else {
        // Doesn't fit either side - center horizontally on cursor
        tooltipX = pageX - tooltipWidth / 2;
        // Clamp to bounds
        tooltipX = Math.max(boundsLeft, Math.min(tooltipX, boundsRight - tooltipWidth));
      }
      
      // Vertical positioning - on mobile, prefer above the finger
      if (isMobile) {
        // Mobile: prefer above the touch point
        if (pageY - offset - tooltipHeight >= boundsTop) {
          // Fits above
          tooltipY = pageY - tooltipHeight - offset;
        } else if (pageY + offset + tooltipHeight <= boundsBottom) {
          // Fits below
          tooltipY = pageY + offset;
        } else {
          // Doesn't fit either direction - position at top of screen
          tooltipY = boundsTop;
        }
      } else {
        // Desktop: prefer below the cursor
        if (pageY + offset + tooltipHeight <= boundsBottom) {
          // Fits below
          tooltipY = pageY + offset;
        } else if (pageY - offset - tooltipHeight >= boundsTop) {
          // Fits above
          tooltipY = pageY - tooltipHeight - offset;
        } else {
          // Doesn't fit either direction - center vertically on cursor
          tooltipY = pageY - tooltipHeight / 2;
          // Clamp to bounds
          tooltipY = Math.max(boundsTop, Math.min(tooltipY, boundsBottom - tooltipHeight));
        }
      }
      
      // Update position
      tooltip
        .style("visibility", "visible")
        .style("top", tooltipY + "px")
        .style("left", tooltipX + "px");
      
      // Only update content if enough time has passed (throttle)
      if (shouldUpdateContent) {
        let nearbySongs: Array<{ name: string; artist: string; rank: number; valence: number; energy: number; albumImage?: string; spotifyId?: string }> = [];
        if (heatmapData.unifiedGrid && heatmapData.unifiedGrid.length > 0) {
          nearbySongs = getSongsInRadius(heatmapData.unifiedGrid, calculatedValence, calculatedEnergy, 0.05);
        }

        const currentModalContext = {
          valence: calculatedValence,
          energy: calculatedEnergy,
          nearbySongs: nearbySongs.map(song => ({
            id: song.spotifyId || '',
            name: song.name,
            artist: song.artist,
            spotifyId: song.spotifyId || ''
          }))
        };

        const tc = isDark ? '#E0E0E0' : '#000';
        const tcSub = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
        const tcMuted = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';
        const borderClr = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
        const songBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
        const songBgHover = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';
        const btnBg = isDark ? '#fff' : '#000';
        const btnColor = isDark ? '#000' : '#fff';
        const closeHover = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)';
        const placeholderBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';

        let tooltipContent = `
          <div style="margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; gap: 8px;">
            <div style="color: ${tc}; flex: 1;">
              <strong>Valence:</strong> ${calculatedValence.toFixed(2)} &nbsp;&nbsp;
              <strong>Energy:</strong> ${calculatedEnergy.toFixed(2)}
            </div>
            <button onclick="window.closeTooltip()" style="background: transparent; border: none; color: ${tc}; padding: 4px; cursor: pointer; flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; transition: background 0.2s;" onmouseover="this.style.background='${closeHover}'" onmouseout="this.style.background='transparent'">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </button>
          </div>
        `;

        if (nearbySongs.length > 0) {
          tooltipContent += `<div style="border-top: 1px solid ${borderClr}; padding-top: 8px; margin-top: 8px;">`;
          tooltipContent += `<strong style="display: block; margin-bottom: 6px; color: ${tc};">Songs nearby (${nearbySongs.length}):</strong>`;
          nearbySongs.slice(0, 5).forEach((song: any, index: number) => {
            const albumArt = song.albumImage
              ? `<img src="${song.albumImage}" style="width: 40px; height: 40px; border-radius: 4px; margin-right: 8px; object-fit: cover;" />`
              : `<div style="width: 40px; height: 40px; border-radius: 4px; margin-right: 8px; background: ${placeholderBg}; display: flex; align-items: center; justify-content: center;">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${tcSub}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                </div>`;

            const spotifyLink = song.spotifyId
              ? (musicPlatform === 'ytmusic'
                ? `https://music.youtube.com/search?q=${encodeURIComponent(song.name + ' ' + song.artist)}`
                : `https://open.spotify.com/track/${song.spotifyId}`)
              : null;

            const songContent = `
              <div style="flex: 1;">
                <div style="font-weight: 500; margin-bottom: 2px; color: ${tc};">${song.name}</div>
                <div style="font-size: 10px; color: ${tcSub};">${song.artist}</div>
              </div>
            `;

            if (spotifyLink) {
              tooltipContent += `
                <a href="${spotifyLink}" target="_blank" rel="noopener noreferrer" style="text-decoration: none; color: inherit; display: flex; margin-bottom: 6px; padding: 6px; background: ${songBg}; border-radius: 4px; align-items: center; transition: background 0.2s;" onmouseover="this.style.background='${songBgHover}'" onmouseout="this.style.background='${songBg}'">
                  ${albumArt}
                  ${songContent}
                </a>
              `;
            } else {
              tooltipContent += `
                <div style="margin-bottom: 6px; padding: 6px; background: ${songBg}; border-radius: 4px; display: flex; align-items: center;">
                  ${albumArt}
                  ${songContent}
                </div>
              `;
            }
          });

          if (nearbySongs.length > 5) {
            tooltipContent += `<div style="font-size: 10px; color: ${tcMuted}; margin-top: 4px; font-style: italic;">...and ${nearbySongs.length - 5} more</div>`;
          }
          tooltipContent += '</div>';

          tooltipContent += `
            <div style="border-top: 1px solid ${borderClr}; padding-top: 12px; margin-top: 12px;">
              <button
                onclick="window.openRecommendationsModal(${JSON.stringify(currentModalContext).replace(/"/g, '&quot;')})"
                style="
                  width: 100%;
                  padding: 10px 16px;
                  background: ${btnBg};
                  color: ${btnColor};
                  border: none;
                  border-radius: 100px;
                  font-weight: 600;
                  font-size: 13px;
                  cursor: pointer;
                  font-family: inherit;
                  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 6px;
                "
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
                Get Recommendations
              </button>
            </div>
          `;
        } else {
          tooltipContent += `
            <div style="border-top: 1px solid ${borderClr}; padding-top: 12px; margin-top: 12px;">
              <button
                onclick="window.openRecommendationsModal(${JSON.stringify(currentModalContext).replace(/"/g, '&quot;')})"
                style="
                  width: 100%;
                  padding: 10px 16px;
                  background: ${btnBg};
                  color: ${btnColor};
                  border: none;
                  border-radius: 100px;
                  font-weight: 600;
                  font-size: 13px;
                  cursor: pointer;
                  font-family: inherit;
                  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 6px;
                "
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
                Get Recommendations
              </button>
            </div>
          `;
        }

        tooltip.html(tooltipContent);
      }
      
      // Clear existing timer and set new one
      if (tooltipHideTimerRef.current) {
        clearTimeout(tooltipHideTimerRef.current);
      }
      
      // Set new timer to hide tooltip after 5 seconds (only if not pinned)
      if (!tooltipPinnedRef.current) {
        tooltipHideTimerRef.current = setTimeout(() => {
          if (!tooltipPinnedRef.current) {
            tooltip.style("visibility", "hidden");
          }
        }, 5000);
      }
    };

    // Canvas mouse interactions
    const handleMouseMove = (event: MouseEvent) => {
      if (!tooltipPinnedRef.current) {
        updateTooltip(event.clientX, event.clientY, event.pageX, event.pageY);
      }
    };

    const handleMouseLeave = () => {
      if (!tooltipPinnedRef.current) {
        // Clear the timer when mouse leaves
        if (tooltipHideTimerRef.current) {
          clearTimeout(tooltipHideTimerRef.current);
        }
        tooltip.style("visibility", "hidden");
      }
    };

    const handleClick = (event: MouseEvent) => {
      // Toggle pinned state on click
      const newPinnedState = !tooltipPinnedRef.current;
      tooltipPinnedRef.current = newPinnedState;
      setTooltipPinned(newPinnedState);

      if (newPinnedState) {
        // Just pinned - update position one more time and start 5 second timer
        updateTooltip(event.clientX, event.clientY, event.pageX, event.pageY);
        // Even when pinned, hide after 5 seconds
        if (tooltipHideTimerRef.current) {
          clearTimeout(tooltipHideTimerRef.current);
        }
        tooltipHideTimerRef.current = setTimeout(() => {
          tooltip.style("visibility", "hidden");
          tooltipPinnedRef.current = false;
          setTooltipPinned(false);
        }, 5000);
      } else {
        // Unpinned - hide the tooltip
        tooltip.style("visibility", "hidden");
        if (tooltipHideTimerRef.current) {
          clearTimeout(tooltipHideTimerRef.current);
        }
      }
    };

    // Touch event handlers for mobile
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 1 && !tooltipPinnedRef.current) {
        const touch = event.touches[0];
        updateTooltip(touch.clientX, touch.clientY, touch.pageX, touch.pageY);
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length === 1 && !tooltipPinnedRef.current) {
        event.preventDefault(); // Prevent scrolling while interacting
        const touch = event.touches[0];
        updateTooltip(touch.clientX, touch.clientY, touch.pageX, touch.pageY);
      }
    };

    const handleTouchEnd = () => {
      // Touch end doesn't auto-pin - tooltip will auto-hide after 5 seconds
    };
    
    // Global function to close tooltip (called from tooltip close button)
    (window as any).closeTooltip = () => {
      tooltipPinnedRef.current = false;
      setTooltipPinned(false);
      tooltip.style("visibility", "hidden");
      // Clear any pending hide timer
      if (tooltipHideTimerRef.current) {
        clearTimeout(tooltipHideTimerRef.current);
      }
    };
    
    // Global function to open recommendations modal (called from tooltip button)
    (window as any).openRecommendationsModal = async (context: any) => {
      setModalContext(context);
      setShowRecommendationsModal(true);
      // Close the tooltip when opening modal
      tooltipPinnedRef.current = false;
      setTooltipPinned(false);
      tooltip.style("visibility", "hidden");
      if (tooltipHideTimerRef.current) {
        clearTimeout(tooltipHideTimerRef.current);
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Clear tooltip hide timer on cleanup
      if (tooltipHideTimerRef.current) {
        clearTimeout(tooltipHideTimerRef.current);
      }
    };
    // NOTE: tooltipPinned is intentionally NOT a dependency — handlers read it via
    // tooltipPinnedRef. Including it re-ran this effect on every pin/unpin, resetting
    // the canvas backing store and causing a visible flicker.
  }, [currentTimeRange, precomputedData, timeRangeOptions, isTransitioning, canvasSize, playlistHeatmapData, isDark]);

  const isMobileView = canvasSize.width < 700;

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px' }}>

      {/* Subheading above heatmap */}
      <div style={{
        textAlign: 'center',
        margin: '0 0 24px 0',
      }}>
        <div style={{
          position: 'relative',
          minHeight: isMobileView ? '24px' : '28px',
          display: 'flex',
          justifyContent: 'center',
        }}>
          {/* Current subheading - fades out when animating */}
          <div
            key={`current-${currentSubheading}`}
            style={{
              position: isSubheadingAnimating ? 'absolute' : 'relative',
              fontSize: isMobileView ? '18px' : '20px',
              fontWeight: 500,
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              opacity: isSubheadingAnimating ? 0 : 1,
              transform: isSubheadingAnimating ? 'translateY(-20px)' : 'translateY(0)',
              transition: `opacity ${TRANSITION_DURATION / 2}ms ease-in, transform ${TRANSITION_DURATION / 2}ms ease-in`,
              whiteSpace: 'nowrap',
            }}
          >
            {currentSubheading}
          </div>
          {/* Next subheading - fades in when animating */}
          {isSubheadingAnimating && nextSubheading && (
            <div
              key={`next-${nextSubheading}`}
              style={{
                position: 'absolute',
                top: 0,
                fontSize: isMobileView ? '18px' : '20px',
                fontWeight: 500,
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                animation: `slideUpFadeIn ${TRANSITION_DURATION / 2}ms ease-out ${TRANSITION_DURATION / 2}ms backwards`,
                whiteSpace: 'nowrap',
              }}
            >
              {nextSubheading}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUpFadeIn {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideUpFadeOut {
          0% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-20px);
          }
        }
      `}</style>

      {/* Heatmap Card with rounded corner mask */}
      <div style={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        opacity: heatmapVisible ? 1 : 0,
        transition: 'opacity 1s ease-out',
      }}>
        <div style={{ 
          position: 'relative', 
          width: `${canvasSize.width}px`, 
          height: `${canvasSize.height}px`,
          borderRadius: '24px',
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
          background: isDark ? '#1B1B30' : '#ffffff',
          padding: '0',
          overflow: 'hidden',
        }}>
          <canvas 
            ref={canvasRef}
            style={{ 
              position: 'absolute',
              top: isMobileView ? '30px' : '50px',
              left: isMobileView ? '30px' : '50px',
              cursor: 'crosshair',
              opacity: 1,
              transition: 'opacity 0.3s ease',
              touchAction: 'none',
              pointerEvents: 'auto',
            }}
          />
          <svg
            ref={svgRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              pointerEvents: 'none',
              opacity: 1,
              transition: 'opacity 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Axis legend — explains the two dimensions so first-timers (and anyone
          who receives a shared screenshot) can read the map. Quiet, pill-styled,
          never competing with the heatmap. */}
      <div
        aria-label="How to read the heatmap: horizontal axis is valence from sad to happy, vertical axis is energy from calm to intense"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: isMobileView ? '8px' : '12px',
          margin: '0 auto',
          padding: '0 20px',
        }}
      >
        {[
          { axis: '↔', label: 'Valence', range: 'sad → happy' },
          { axis: '↕', label: 'Energy', range: 'calm → intense' },
        ].map(({ axis, label, range }) => (
          <div
            key={label}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: isMobileView ? '6px 12px' : '7px 16px',
              borderRadius: '100px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
              fontSize: isMobileView ? '12px' : '13px',
              whiteSpace: 'nowrap',
            }}
          >
            <span aria-hidden="true" style={{ fontWeight: 700, color: isDark ? '#94A3B8' : '#64748B' }}>{axis}</span>
            <span style={{ fontWeight: 600, color: isDark ? '#F8FAFC' : '#0F172A' }}>{label}</span>
            <span style={{ color: isDark ? '#94A3B8' : '#64748B' }}>{range}</span>
          </div>
        ))}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowShareModal(false)}
        >
          <div 
            style={{
              backgroundColor: isDark ? '#1B1B30' : '#fff',
              borderRadius: '24px',
              padding: isMobileView ? '32px 24px' : '40px 32px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.2)',
              border: isDark ? '1px solid rgba(255,255,255,0.08)' : 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <h3 style={{
                fontSize: isMobileView ? '20px' : '22px',
                fontWeight: 700,
                margin: 0,
                color: isDark ? '#E0E0E0' : '#000',
              }}>
                Share your heatmap
              </h3>
              <button
                onClick={() => setShowShareModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                  lineHeight: 1,
                }}
                title="Close"
              >
                ×
              </button>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}>
              {/* Download Image */}
              <button
                onClick={handleDownloadImage}
                style={{
                  padding: '16px 20px',
                  borderRadius: '12px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                  backgroundColor: isDark ? '#252545' : '#fff',
                  color: isDark ? '#E0E0E0' : '#000',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#2d2d55' : '#f8f8f8';
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#2a2a2a' : '#fff';
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
                }}
              >
                <DownloadIcon style={{ width: 20, height: 20 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>Download Image</div>
                  <div style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', marginTop: '2px' }}>
                    Save to your device
                  </div>
                </div>
              </button>

              {/* Share Image */}
              <button
                onClick={() => handleShareImage()}
                style={{
                  padding: '16px 20px',
                  borderRadius: '12px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                  backgroundColor: isDark ? '#252545' : '#fff',
                  color: isDark ? '#E0E0E0' : '#000',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#2d2d55' : '#f8f8f8';
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#2a2a2a' : '#fff';
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
                }}
              >
                <ShareIcon style={{ width: 20, height: 20 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>Share Image</div>
                  <div style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', marginTop: '2px' }}>
                    Share via apps or social media
                  </div>
                </div>
              </button>

              {/* Save as Playlist Cover */}
              <button
                onClick={handleSaveCover}
                style={{
                  padding: '16px 20px',
                  borderRadius: '12px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                  backgroundColor: isDark ? '#252545' : '#fff',
                  color: isDark ? '#E0E0E0' : '#000',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#2d2d55' : '#f8f8f8';
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#2a2a2a' : '#fff';
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
                }}
              >
                <AlbumIcon style={{ width: 20, height: 20 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>Save as Playlist Cover</div>
                  <div style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', marginTop: '2px' }}>
                    Square art with emotify.dev watermark
                  </div>
                </div>
              </button>

              {/* Share to Instagram */}
              <button
                onClick={() => handleShareImage('instagram')}
                style={{
                  padding: '16px 20px',
                  borderRadius: '12px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                  backgroundColor: isDark ? '#252545' : '#fff',
                  color: isDark ? '#E0E0E0' : '#000',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#2d2d55' : '#f8f8f8';
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#2a2a2a' : '#fff';
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
                }}
              >
                <InstagramIcon style={{ width: 20, height: 20 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>Share to Instagram</div>
                  <div style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', marginTop: '2px' }}>
                    Optimized for Instagram Stories
                  </div>
                </div>
              </button>

            </div>
          </div>
        </div>
      )}

      {/* Music Analysis */}
      {analysis && (
        <div style={{
          maxWidth: '700px',
          padding: isMobileView ? '0 20px' : '0 40px',
          textAlign: 'center',
        }}>
          {/* Subordinate section label — quiet so it introduces the analysis
              without competing with the page title or the heatmap for attention. */}
          <h3 style={{
            fontSize: isMobileView ? '16px' : '18px',
            fontWeight: 600,
            marginBottom: '12px',
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
            letterSpacing: '-0.01em',
          }}>
            The soundtrack of your emotions
          </h3>
          <p style={{
            fontSize: isMobileView ? '15px' : '16px',
            color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.75)',
            lineHeight: '1.7',
            marginBottom: '24px',
          }}>
            {analysis.description}. {analysis.diversityDescription}
            {analysis.sampleSongs && analysis.sampleSongs.length > 0 && (
              <>, with tracks like "{analysis.sampleSongs[0]}"
              {analysis.sampleSongs[1] && `, "${analysis.sampleSongs[1]}"`}
              {analysis.sampleSongs[2] && `, and "${analysis.sampleSongs[2]}"`} painting the picture</>
            )}.
          </p>

          {/* Share button */}
          <button
            onClick={() => setShowShareModal(true)}
            disabled={isGeneratingImage || false}
            style={{
              padding: isMobileView ? '12px 24px' : '14px 32px',
              borderRadius: '100px',
              border: 'none',
              backgroundColor: (isGeneratingImage) ? (isDark ? 'rgba(255,255,255,0.1)' : '#f5f5f5') : (isDark ? '#fff' : '#000'),
              color: (isGeneratingImage) ? (isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)') : (isDark ? '#000' : '#fff'),
              fontWeight: 600,
              cursor: (isGeneratingImage) ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              fontSize: isMobileView ? '13px' : '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'inherit',
              boxShadow: (isGeneratingImage) ? 'none' : '0 2px 8px rgba(0,0,0,0.15)',
            }}
            title="Share your heatmap"
            onMouseEnter={(e) => {
              if (!isGeneratingImage && !false) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = (isGeneratingImage) ? 'none' : '0 2px 8px rgba(0,0,0,0.15)';
            }}
          >
            {(isGeneratingImage) ? (
              <>
                <CircularProgress size={16} style={{ color: 'currentColor' }} />
                Generating...
              </>
            ) : (
              <>
                <ShareIcon style={{ width: 16, height: 16 }} />
                Share
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Recommendations Modal */}
      {showRecommendationsModal && modalContext && (
        <RecommendationsModal
          isOpen={showRecommendationsModal}
          onClose={() => setShowRecommendationsModal(false)}
          onAuthError={() => {
            setShowRecommendationsModal(false);
          }}
          valence={modalContext.valence}
          energy={modalContext.energy}
          nearbySongs={modalContext.nearbySongs}
          audioFeatures={audioFeatures || []}
        />
      )}
    </div>
  );
};

export default Heatmap;
